import React, { FC } from 'react';
import TableSimple from 'src/components/TableSimple';
import { CustomRenderType, TableColumnType, TableRowType } from 'src/types/table';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import translate from '@translate';
import { TransportPackageType } from 'src/types/transport';
import { colors, sizes } from 'src/themes/default';
import TextFieldDebounced from 'src/components/TextFieldDebounced';
import Select from 'src/components/Select';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { FormErrorsType } from '../../types/global';

export type EditKeysType =
    | 'WEIGHT'
    | 'WIDTH'
    | 'HEIGHT'
    | 'LENGTH'
    | 'PRICE'
    | 'EXPEDITION_BOX_NUMBER'
    | 'PACKAGE_TYPE'
    | 'VOLUME'
    | 'LOADING_METERS';

const allColumns: TableColumnType[] = [
    { key: 'STATUS', label: 'status', type: 'select', selectOption: 'transportStatusCodes' },
    {
        key: 'PACKAGE_TYPE',
        label: 'packageType',
        customRender: true,
    },
    {
        key: 'WAREHOUSE_TRACKING_NUMBER',
        label: 'warehouseTrackingNumber',
        customRender: true,
        defaultShow: true,
    },
    {
        key: 'WEIGHT',
        label: 'weight',
        customRender: true,
    },
    {
        key: 'VOLUME',
        label: 'volume',
        customRender: true,
    },
    {
        key: 'LOADING_METERS',
        label: 'loadingMeters',
        customRender: true,
    },
    {
        key: 'WIDTH',
        label: 'width',
        customRender: true,
    },
    {
        key: 'HEIGHT',
        label: 'height',
        customRender: true,
    },
    {
        key: 'LENGTH',
        label: 'length',
        customRender: true,
    },
    {
        key: 'PRICE',
        label: 'price',
        customRender: true,
    },
    {
        key: 'EXPEDITION_BOX_NUMBER',
        label: 'boxNumber',
        customRender: true,
    },
];

export interface PackagesTablePropsType {
    data?: TransportPackageType[];
    newTransport: boolean;
    disableEdit: boolean;
    onChange: (value: TransportPackageType[]) => void;
    editErrors: FormErrorsType;
    transportStatusCodes: ConstantsCodeType[];
    packageTypeCodes: ConstantsCodeType[];
    shipper?: string | null;
}

const PackagesTable: FC<PackagesTablePropsType> = (props) => {
    const getInputName = (id: number, key: string) => `package-${id}-${key}`;

    const changeValue = (key: EditKeysType, value: unknown, error: boolean, id: number) => {
        const newData = props.data?.map((packageItem) => {
            if (packageItem.ID === id) {
                const updatedPackage = { ...packageItem };
                updatedPackage[key] = value as never;
                return updatedPackage;
            }
            return packageItem;
        });
        props.onChange(newData || []);
    };

    const handleToggleRow = (row: TableRowType) => {
        if (row.ID < 0) {
            props.onChange(props.data?.filter((item) => item.ID != row.ID) || []);
        } else {
            props.onChange(
                props.data?.map((item) => {
                    if (item.ID === row.ID) {
                        return { ...item, LINE_STATE: row.LINE_STATE === 'D' ? 'E' : 'D' };
                    }
                    return item;
                }) || []
            );
        }
    };

    const customRender: CustomRenderType = (column, row) => {
        const name = getInputName(row.ID, column.key);
        switch (column.key) {
            case 'PACKAGE_TYPE':
                return (
                    <Select
                        options={props.packageTypeCodes}
                        value={row.PACKAGE_TYPE}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('PACKAGE_TYPE', value, error, row.ID)}
                        name={name}
                        fullWidth
                        disabled={props.disableEdit}
                    />
                );
            case 'WAREHOUSE_TRACKING_NUMBER': {
                const warehouseTracking = row.WAREHOUSE_TRACKING_NUMBER
                    ? String(row.WAREHOUSE_TRACKING_NUMBER)
                    : '';
                return (
                    <span data-testid={`package-${row.ID}-warehouse-tracking`}>
                        {warehouseTracking || '—'}
                    </span>
                );
            }
            case 'WEIGHT':
                return (
                    <TextFieldDebounced
                        value={row.WEIGHT}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('WEIGHT', value, error, row.ID)}
                        name={name}
                        fullWidth
                        type="numberLocale"
                        min={0}
                        disabled={props.disableEdit}
                    />
                );
            case 'VOLUME':
                return (
                    <TextFieldDebounced
                        value={row.VOLUME}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('VOLUME', value, error, row.ID)}
                        name={name}
                        fullWidth
                        type="numberLocale"
                        min={0}
                        disabled={props.disableEdit}
                    />
                );
            case 'LOADING_METERS':
                return (
                    <TextFieldDebounced
                        value={row.LOADING_METERS}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('LOADING_METERS', value, error, row.ID)}
                        name={name}
                        fullWidth
                        type="numberLocale"
                        min={0}
                        disabled={props.disableEdit}
                    />
                );
            case 'WIDTH':
                return (
                    <TextFieldDebounced
                        value={row.WIDTH}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('WIDTH', value, error, row.ID)}
                        name={name}
                        fullWidth
                        type="numberLocale"
                        min={0}
                        disabled={props.disableEdit}
                    />
                );
            case 'HEIGHT':
                return (
                    <TextFieldDebounced
                        value={row.HEIGHT}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('HEIGHT', value, error, row.ID)}
                        name={name}
                        fullWidth
                        type="numberLocale"
                        min={0}
                        disabled={props.disableEdit}
                    />
                );
            case 'LENGTH':
                return (
                    <TextFieldDebounced
                        value={row.LENGTH}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('LENGTH', value, error, row.ID)}
                        name={name}
                        fullWidth
                        type="numberLocale"
                        min={0}
                        disabled={props.disableEdit}
                    />
                );
            case 'PRICE':
                return (
                    <TextFieldDebounced
                        value={row.PRICE}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('PRICE', value, error, row.ID)}
                        name={name}
                        fullWidth
                        type="numberLocale"
                        min={0}
                        disabled={props.disableEdit}
                    />
                );
            case 'EXPEDITION_BOX_NUMBER':
                return (
                    <TextFieldDebounced
                        value={row.EXPEDITION_BOX_NUMBER}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('EXPEDITION_BOX_NUMBER', value, error, row.ID)}
                        name={name}
                        fullWidth
                        type="numberLocale"
                        min={0}
                        disabled={props.disableEdit}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <TableSimple
            rowIdKey="ID"
            rows={props.data || []}
            columsLabelPrefix="transportDetail.packageTable"
            allColumns={allColumns}
            initSort={{ column: 'ID', direction: 'ASC', createIdNumber: true }}
            rowActions={[
                {
                    icon: <HighlightOffIcon />,
                    label: translate('transportDetail.action.removePackage'),
                    onClick: handleToggleRow,
                    hiddenFce: (row) => !!props.disableEdit || row.LINE_STATE === 'D',
                    style: { height: sizes.table_action_with_input_H },
                },
                {
                    icon: <AddCircleIcon />,
                    label: translate('transportDetail.action.addPackage'),
                    onClick: handleToggleRow,
                    hiddenFce: (row) => !!props.disableEdit || row.LINE_STATE === 'E',
                    style: { height: sizes.table_action_with_input_H },
                },
            ]}
            selectOptions={{ transportStatusCodes: props.transportStatusCodes }}
            customRender={customRender}
            rowSxFce={(row) =>
                row.LINE_STATE === 'D'
                    ? { color: colors.text_T3, '& .MuiTableCell-root': { color: colors.text_T3 } }
                    : {}
            }
            disableFilter
            sumCols={{
                WEIGHT: true,
                VOLUME: true,
            }}
            sumColsFilter={(row) => row.LINE_STATE === 'E'}
            maxHeight={'unset'}
        />
    );
};

export default PackagesTable;
